import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { copyText, copyAsMarkdown, asMarkdown } from "@/lib/clipboard";
import { Plus, Trash, PencilSimple, Sparkle, X, Lock, LockOpen, DotsSixVertical, Copy, MarkdownLogo, CaretDown, CaretUp, CaretDoubleDown, CaretDoubleUp, TrashSimple } from "@phosphor-icons/react";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/** Split a comma/semicolon/newline-separated skills string into trimmed chips. */
function splitSkills(s) {
  if (!s) return [];
  return String(s)
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Format ISO-ish date strings into a friendlier display.
 *  Accepts: "2024-09-15" → "15 Sep 2024"
 *           "2024-09"    → "Sep 2024"
 *           "2024"       → "2024"
 *           ""           → "?"
 *  Anything else falls through unchanged. */
export function formatDateDisplay(s) {
  if (!s) return "?";
  const str = String(s).trim();
  const m = str.match(/^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?$/);
  if (!m) return str;
  const [, y, mo, d] = m;
  if (d) return `${parseInt(d, 10)} ${MONTHS_SHORT[parseInt(mo, 10) - 1] || ""} ${y}`.trim();
  if (mo) return `${MONTHS_SHORT[parseInt(mo, 10) - 1] || ""} ${y}`.trim();
  return y;
}

// Per-resource "kind" display label overrides — used to keep legacy keys
// working while showing friendlier copy. e.g. an entry stored as kind="job"
// shows as "Permanent" without forcing a destructive migration.
const KIND_LABEL_OVERRIDES = {
  work: { job: "Permanent" },
  education: {},
};
function displayKind(resource, kind) {
  if (!kind) return "—";
  return KIND_LABEL_OVERRIDES[resource]?.[kind] || kind;
}

// Field metadata: [key, label, type, options?]
const SCHEMAS = {
  work: {
    title: "Work Experience",
    api: "/work",
    helper: "permanent roles, contracts, apprenticeships, internships, freelance, volunteering — choose the type for each.",
    fields: [
      ["kind", "Type", "select", [
        "Permanent",
        "Contract",
        "Part-time",
        "Apprenticeship",
        "Internship",
        "Placement / Year in industry",
        "Freelance / Self-employed",
        "Volunteer",
      ]],
      ["role", "Role", "input"],
      ["employer", "Employer", "input"],
      ["location", "Location", "input"],
      ["start_date", "Start date", "date"],
      ["end_date", "End date", "date"],
      ["current", "Still working here", "checkbox"],
      ["responsibilities", "Responsibilities", "textarea"],
      ["skills", "Skills", "input", null, { placeholder: "e.g. Python, SQL, customer service", hint: "separate with commas — they'll show as chips." }],
    ],
    primary: (i) => `${i.role} · ${i.employer}`,
    secondary: (i) => `${displayKind("work", i.kind)} · ${formatDateDisplay(i.start_date)} — ${i.current ? "now" : formatDateDisplay(i.end_date)}`,
    copyText: (i) =>
      `${i.role} · ${i.employer}\n${formatDateDisplay(i.start_date)} — ${i.current ? "now" : formatDateDisplay(i.end_date)}` +
      (i.location ? ` · ${i.location}` : "") +
      (i.responsibilities ? `\n\n${i.responsibilities}` : "") +
      (i.skills ? `\n\nSkills: ${i.skills}` : ""),
  },
  education: {
    title: "Education",
    api: "/education",
    helper: "GCSEs, A-Levels, BTECs, T-Levels, NVQs, apprenticeships, degrees, bootcamps, courses, certificates. Add one entry per institution; list each subject/module as its own row below.",
    fields: [
      ["qualification", "Programme", "input", null, { placeholder: "e.g. GCSEs, A-Levels, BSc Politics" }],
      ["provider", "Institution", "input", null, { placeholder: "e.g. Riddlesdown Collegiate" }],
      ["location", "Town / city", "input", null, { placeholder: "e.g. Croydon" }],
      ["start_date", "From", "date"],
      ["end_date", "To", "date"],
      ["subjects", "Subjects / modules", "subjects-table", null, { hint: "one row per subject — exactly how job application forms ask for them. use the rpt button to repeat a row's grade/year/level." }],
    ],
    primary: (i) => `${i.qualification || "Programme"} · ${i.provider || "?"}`,
    secondary: (i) => {
      const parts = [];
      if (i.location) parts.push(i.location);
      const dr = `${formatDateDisplay(i.start_date)} — ${formatDateDisplay(i.end_date)}`;
      if (dr.trim() !== "—") parts.push(dr);
      if (i.subjects?.length) parts.push(`${i.subjects.length} subject${i.subjects.length === 1 ? "" : "s"}`);
      // Legacy data — only show kind/grade if we actually have them and they aren't the default
      if (i.kind && i.kind !== "degree" && (!i.subjects || i.subjects.length === 0)) {
        parts.unshift(displayKind("education", i.kind));
      }
      if (i.grade && (!i.subjects || i.subjects.length === 0)) parts.push(i.grade);
      return parts.filter(Boolean).join(" · ");
    },
    copyText: (i) => {
      const head =
        `${i.qualification || ""} · ${i.provider || ""}` +
        (i.location ? `\n${i.location}` : "") +
        `\n${formatDateDisplay(i.start_date)} — ${formatDateDisplay(i.end_date)}` +
        (i.grade && (!i.subjects || i.subjects.length === 0) ? `\nGrade: ${i.grade}` : "") +
        (i.awarding_body ? `\nAwarding body: ${i.awarding_body}` : "");
      if (!i.subjects?.length) return head;
      const rows = i.subjects
        .filter((s) => s.subject || s.grade || s.year || s.level)
        .map((s) => `  - ${s.subject || "?"} · ${s.grade || "—"} · ${s.year || "—"} · ${s.level || "—"}`)
        .join("\n");
      return `${head}\n\nSubjects:\n${rows}`;
    },
  },
};

/** Subjects/modules sub-table — used inside an Education entry.
 *
 *  Renders the same layout job-application forms use:
 *    subject name · grade · year taken · level
 *  Each row is an inline editable form with add/delete buttons. The whole
 *  thing serialises into the parent's `subjects` array.
 */
const LEVEL_OPTIONS = [
  "GCSE",
  "A-Level",
  "AS-Level",
  "BTEC",
  "T-Level",
  "NVQ",
  "IB",
  "International",
  "Module",
  "Other",
];

function SubjectsTableEditor({ value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, patch) => {
    const next = rows.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => onChange([...rows, { subject: "", grade: "", year: "", level: rows[rows.length - 1]?.level || "GCSE" }]);
  const repeat = (i) => {
    const src = rows[i] || {};
    onChange([...rows, { subject: "", grade: src.grade || "", year: src.year || "", level: src.level || "GCSE" }]);
  };
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));

  return (
    <div data-testid="subjects-table-editor" className="space-y-1.5">
      {rows.length > 0 && (
        <div className="grid grid-cols-[1fr_90px_90px_140px_auto] gap-2 px-1">
          <span className="label-tiny">subject</span>
          <span className="label-tiny">grade</span>
          <span className="label-tiny">year</span>
          <span className="label-tiny">level</span>
          <span className="w-[68px]"/>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_90px_90px_140px_auto] gap-2" data-testid={`subject-row-${i}`}>
          <input
            data-testid={`subject-name-${i}`}
            value={r.subject || ""}
            onChange={(e) => update(i, { subject: e.target.value })}
            placeholder="e.g. English Literature"
            className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm"
          />
          <input
            data-testid={`subject-grade-${i}`}
            value={r.grade || ""}
            onChange={(e) => update(i, { grade: e.target.value })}
            placeholder="A*"
            className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"
          />
          <input
            data-testid={`subject-year-${i}`}
            value={r.year || ""}
            onChange={(e) => update(i, { year: e.target.value })}
            placeholder="2015"
            inputMode="numeric"
            maxLength={4}
            className="card-hard rounded-md px-2.5 py-1.5 bg-white text-sm text-center"
          />
          <select
            data-testid={`subject-level-${i}`}
            value={r.level || "GCSE"}
            onChange={(e) => update(i, { level: e.target.value })}
            className="card-hard rounded-md px-2 py-1.5 bg-white text-sm"
          >
            {LEVEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            {r.level && !LEVEL_OPTIONS.includes(r.level) && (
              <option value={r.level}>{r.level}</option>
            )}
          </select>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`subject-repeat-${i}`}
              onClick={() => repeat(i)}
              title="duplicate this row's grade, year & level"
              className="btn-hard bg-white rounded-md px-2 py-1.5 text-[10px] font-mono-d"
            >rpt</button>
            <button
              type="button"
              data-testid={`subject-remove-${i}`}
              onClick={() => remove(i)}
              title="remove row"
              className="p-1.5 hover:bg-[var(--brand-bg)] rounded"
            ><Trash size={14}/></button>
          </div>
        </div>
      ))}
      <button
        type="button"
        data-testid="subject-add"
        onClick={add}
        className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5 mt-1"
      >
        <Plus size={12} weight="bold"/> add subject
      </button>
    </div>
  );
}

/** Read-only Subjects/modules table — shown when the Education entry is expanded. */
function SubjectsTableView({ subjects, resource, itemId }) {
  if (!subjects?.length) {
    return <span className="font-mono-d text-xs text-[var(--brand-muted)]">no subjects added yet</span>;
  }
  const allText = subjects
    .filter((s) => s.subject || s.grade || s.year || s.level)
    .map((s) => `${s.subject || "?"} · ${s.grade || "—"} · ${s.year || "—"} · ${s.level || "—"}`)
    .join("\n");

  return (
    <div className="w-full min-w-0" data-testid={`subjects-view-${itemId}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-[1.5px] border-[var(--brand-ink)]/30">
              <th className="text-left p-1.5 label-tiny font-normal">subject</th>
              <th className="text-left p-1.5 label-tiny font-normal w-[70px]">grade</th>
              <th className="text-left p-1.5 label-tiny font-normal w-[70px]">year</th>
              <th className="text-left p-1.5 label-tiny font-normal w-[100px]">level</th>
              <th className="w-[28px]"></th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((s, i) => {
              const line = `${s.subject || ""} · ${s.grade || ""} · ${s.year || ""} · ${s.level || ""}`;
              return (
                <tr key={i} className="border-b border-[var(--brand-ink)]/10 group" data-testid={`subject-view-row-${itemId}-${i}`}>
                  <td className="p-1.5 break-words">{s.subject || <span className="text-[var(--brand-muted)]">—</span>}</td>
                  <td className="p-1.5 font-mono-d text-center">{s.grade || "—"}</td>
                  <td className="p-1.5 font-mono-d text-center">{s.year || "—"}</td>
                  <td className="p-1.5 font-mono-d text-xs">{s.level || "—"}</td>
                  <td className="p-1.5">
                    <button
                      onClick={() => copyText(line, `${s.subject || "Subject"} copied`)}
                      title="copy this row"
                      data-testid={`subject-view-copy-${itemId}-${i}`}
                      className="opacity-50 group-hover:opacity-100 hover:bg-[var(--brand-bg)] rounded p-1 transition"
                    >
                      <Copy size={12}/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <p className="font-mono-d text-[10px] text-[var(--brand-muted)]">{subjects.length} row{subjects.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => copyText(allText, "All subjects copied")}
          data-testid={`subjects-copy-all-${itemId}`}
          className="btn-hard bg-white rounded-md px-2.5 py-1 text-[10px] flex items-center gap-1"
        >
          <Copy size={11}/> copy whole table
        </button>
      </div>
    </div>
  );
}

/** Per-field "expanded view" — render every populated field as a row with its own copy button. */
function FieldsList({ item, schema, resource }) {
  const rows = schema.fields
    .map(([key, label, type]) => {
      let value = item[key];
      if (type === "checkbox") {
        if (!value) return null;
        return { key, label, type, displayValue: "yes", copyValue: "yes" };
      }
      if (type === "subjects-table") {
        if (!Array.isArray(value) || value.length === 0) return null;
        return { key, label, type, subjects: value };
      }
      if (value === undefined || value === null || value === "") return null;
      const display = type === "date" ? formatDateDisplay(value) : String(value);
      const copyValue = type === "date" ? formatDateDisplay(value) : String(value);
      return { key, label, type, displayValue: display, copyValue };
    })
    .filter(Boolean);

  if (rows.length === 0) {
    return <p className="text-xs text-[var(--brand-muted)] mt-3">no fields filled in yet.</p>;
  }
  return (
    <div className="mt-3 border-t-[1.5px] border-[var(--brand-ink)]/15 pt-3 space-y-2" data-testid={`fields-${resource}-${item.id}`}>
      {rows.map((r) => (
        <div key={r.key}
          data-testid={`field-row-${resource}-${item.id}-${r.key}`}
          className={`grid ${r.type === "subjects-table" ? "grid-cols-[minmax(130px,160px)_1fr]" : "grid-cols-[minmax(130px,160px)_1fr_auto]"} gap-3 items-start text-sm group`}>
          <span className="label-tiny pt-1 break-words leading-tight">{r.label}</span>
          {r.type === "subjects-table" ? (
            <SubjectsTableView subjects={r.subjects} resource={resource} itemId={item.id}/>
          ) : r.key === "skills" ? (
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {splitSkills(r.displayValue).map((s, i) => (
                <span key={`${s}-${i}`}
                  className="inline-flex items-center text-[12px] px-2 py-0.5 rounded-full border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-mint)] font-medium">
                  {s}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[var(--brand-ink)] whitespace-pre-wrap break-words min-w-0">{r.displayValue}</span>
          )}
          {r.type !== "subjects-table" && (
            <button
              onClick={() => copyText(r.copyValue, `${r.label} copied`)}
              data-testid={`field-copy-${resource}-${item.id}-${r.key}`}
              title={`copy ${r.label}`}
              className="opacity-50 group-hover:opacity-100 hover:bg-[var(--pastel-yellow)] rounded p-1 transition shrink-0">
              <Copy size={14}/>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function copyToClipboard(text, label = "Copied") {
  return copyText(text, label);
}

/** Smart date field — supports either a full date (day + month + year) or
 *  month + year only. Toggle decides which is rendered/persisted.
 *  Storage: full → "YYYY-MM-DD" · month-only → "YYYY-MM". */
export function SmartDateField({ resource, fieldKey, value, onChange }) {
  // Detect mode from existing value: 10-char ISO means we have a real day.
  const looksLikeFullDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || "");
  const [exact, setExact] = useState(() => looksLikeFullDate(value));

  // Build year list ±60 from today's year.
  const yNow = new Date().getFullYear();
  const years = [];
  for (let y = yNow + 2; y >= yNow - 60; y--) years.push(y);

  // Parse current value
  const parts = String(value || "").match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/);
  const curYear = parts?.[1] || "";
  const curMonth = parts?.[2] || "";

  // Local state so users can pick month OR year first — the persisted value
  // still requires both, but we don't discard a half-typed pick.
  const [pendingMonth, setPendingMonth] = useState(curMonth);
  const [pendingYear, setPendingYear] = useState(curYear);

  // Reconcile local state whenever the parent value flips (e.g. after CV import)
  useEffect(() => {
    if (curMonth !== pendingMonth) setPendingMonth(curMonth);
    if (curYear !== pendingYear) setPendingYear(curYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMonth, curYear]);

  const commit = (m, y) => {
    if (m && y) onChange(`${y}-${m}`);
    else if (y && !m) onChange(y);            // year-only is acceptable
    else onChange("");                         // month-only is not persisted yet
  };

  const switchMode = (toExact) => {
    setExact(toExact);
    // Convert value safely
    if (toExact) {
      // month-only → keep year+month, leave day empty so the picker requests it
      if (pendingYear && pendingMonth) onChange(`${pendingYear}-${pendingMonth}-01`);
      else onChange("");
    } else {
      // exact → keep year+month, drop day
      if (pendingYear && pendingMonth) onChange(`${pendingYear}-${pendingMonth}`);
      else if (pendingYear) onChange(pendingYear);
      else onChange("");
    }
  };

  return (
    <div>
      {exact ? (
        <input
          type="date"
          data-testid={`field-${resource}-${fieldKey}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full card-hard rounded-md px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
        />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <select
            data-testid={`field-${resource}-${fieldKey}-month`}
            value={pendingMonth}
            onChange={(e) => {
              const m = e.target.value;
              setPendingMonth(m);
              commit(m, pendingYear);
            }}
            className="card-hard rounded-md px-3 py-2 bg-white"
          >
            <option value="">month</option>
            {MONTHS_FULL.map((name, i) => (
              <option key={name} value={String(i + 1).padStart(2, "0")}>{name}</option>
            ))}
          </select>
          <select
            data-testid={`field-${resource}-${fieldKey}-year`}
            value={pendingYear}
            onChange={(e) => {
              const y = e.target.value;
              setPendingYear(y);
              commit(pendingMonth, y);
            }}
            className="card-hard rounded-md px-3 py-2 bg-white"
          >
            <option value="">year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      <label className="mt-1.5 flex items-center gap-2 text-[11px] font-mono-d text-[var(--brand-muted)] cursor-pointer select-none">
        <input
          type="checkbox"
          checked={exact}
          onChange={(e) => switchMode(e.target.checked)}
          data-testid={`field-${resource}-${fieldKey}-exact`}
        />
        <span>i know the exact day</span>
      </label>
    </div>
  );
}

function ResourceSection({ resource, accent }) {
  const schema = SCHEMAS[resource];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteText, setRewriteText] = useState("");
  const [rewriting, setRewriting] = useState(false);
  const [rewriteResult, setRewriteResult] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  // Entries with rich sub-data (e.g. Education subjects) auto-expand on first
  // render so the rows are visible without an extra click. The user can still
  // collapse them manually.
  const [expanded, setExpanded] = useState(() => new Set());
  useEffect(() => {
    setExpanded((cur) => {
      const next = new Set(cur);
      for (const it of items) {
        if (Array.isArray(it.subjects) && it.subjects.length > 0) next.add(it.id);
      }
      return next;
    });
  }, [items]);

  const toggleExpand = (id) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = async () => {
    const { data } = await api.get(schema.api);
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e, id) => { e.preventDefault(); setDragOver(id); };
  const onDragEnd = () => { setDragId(null); setDragOver(null); };

  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData("text/plain") || dragId;
    if (!sourceId || sourceId === targetId) { onDragEnd(); return; }
    const src = items.findIndex((i) => i.id === sourceId);
    const dst = items.findIndex((i) => i.id === targetId);
    if (src < 0 || dst < 0) { onDragEnd(); return; }
    const next = items.slice();
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setItems(next);
    onDragEnd();
    try {
      await api.post(`${schema.api}/reorder`, { ids: next.map((i) => i.id) });
    } catch {
      toast.error("Couldn't save order");
      load();
    }
  };

  // Seed select fields with their first option so what-you-see-is-what-you-save.
  const buildEmptyForm = () => {
    const f = {};
    for (const [key, , type, options] of schema.fields) {
      if (type === "select" && Array.isArray(options) && options.length > 0) {
        f[key] = options[0];
      }
    }
    return f;
  };

  const openNew = () => { setEditing("new"); setForm(buildEmptyForm()); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...it }); };
  const cancel = () => { setEditing(null); setForm({}); };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post(schema.api, form);
        toast.success("Added to vault");
      } else {
        await api.put(`${schema.api}/${editing}`, form);
        toast.success("Updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error("Couldn't save");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this entry?")) return;
    await api.delete(`${schema.api}/${id}`);
    toast.success("Removed");
    load();
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Delete all ${items.length} ${resource} entries? This can't be undone.`)) return;
    try {
      const { data } = await api.delete(`/vault/clear/${resource}`);
      setExpanded(new Set());
      toast.success(`Wiped ${data.deleted} entries`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear");
    }
  };

  const allExpanded = items.length > 0 && expanded.size === items.length;
  const collapseAll = () => setExpanded(new Set());
  const expandAll = () => setExpanded(new Set(items.map((it) => it.id)));

  const doRewrite = async () => {
    setRewriting(true);
    setRewriteResult("");
    try {
      const { data } = await api.post("/ai/rewrite", { text: rewriteText, style: "professional" });
      setRewriteResult(data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI is having a moment, try again");
    } finally {
      setRewriting(false);
    }
  };

  const applyRewrite = () => {
    setForm({ ...form, responsibilities: rewriteResult });
    setRewriteOpen(false);
    setRewriteResult("");
    setRewriteText("");
    toast.success("Pasted into form");
  };

  const editorRef = useRef(null);
  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editing]);

  const renderEditor = () => (
    <div
      ref={editorRef}
      className="border-[1.5px] border-[var(--brand-ink)] rounded-md p-5 bg-[var(--pastel-yellow)]"
      data-testid={`editor-${resource}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="label-tiny">/{editing === "new" ? "new entry" : "edit"}</p>
        <button onClick={cancel} className="p-1 rounded hover:bg-white"><X size={16}/></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {schema.fields.map(([key, label, type, options, opts]) => (
          <div key={key} className={(type === "textarea" || type === "subjects-table") ? "md:col-span-2" : ""}>
            {type !== "checkbox" && (
              <label className="label-tiny block mb-1.5 flex items-center justify-between">
                <span>{label}</span>
                {form[key] && type !== "subjects-table" && (
                  <button type="button" onClick={() => copyToClipboard(String(form[key]))}
                    data-testid={`copy-field-${resource}-${key}`}
                    className="text-[10px] underline text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">copy</button>
                )}
              </label>
            )}
            {type === "subjects-table" ? (
              <SubjectsTableEditor
                value={form[key]}
                onChange={(v) => setForm({ ...form, [key]: v })}
              />
            ) : type === "textarea" ? (
              <div>
                <textarea
                  data-testid={`field-${resource}-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={opts?.placeholder || ""}
                  className="w-full card-hard rounded-md px-3 py-2 min-h-[100px] focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
                />
                <button type="button" onClick={() => { setRewriteOpen(true); setRewriteText(form[key] || ""); }}
                  data-testid={`ai-rewrite-${resource}-${key}`}
                  className="mt-2 btn-hard bg-white rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5">
                  <Sparkle weight="duotone" size={14}/> rewrite with AI
                </button>
              </div>
            ) : type === "select" ? (
              <select
                data-testid={`field-${resource}-${key}`}
                value={form[key] || options[0]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
              >
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
                {/* If the stored value isn't in the option list (legacy data), surface it so users see it. */}
                {form[key] && !options.includes(form[key]) && (
                  <option value={form[key]}>{form[key]} (legacy)</option>
                )}
              </select>
            ) : type === "date" ? (
              <SmartDateField
                resource={resource}
                fieldKey={key}
                value={form[key] || ""}
                onChange={(v) => setForm({ ...form, [key]: v })}
              />
            ) : type === "checkbox" ? (
              <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid={`field-${resource}-${key}`}
                  checked={!!form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">{label}</span>
              </label>
            ) : (
              <input
                data-testid={`field-${resource}-${key}`}
                value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={opts?.placeholder || ""}
                className="w-full card-hard rounded-md px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
              />
            )}
            {opts?.hint && type !== "checkbox" && (
              <p className="text-[10px] font-mono-d text-[var(--brand-muted)] mt-1">{opts.hint}</p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-5 flex gap-3">
        <button onClick={save} data-testid={`save-${resource}`}
          className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-1.5 hover:bg-[var(--brand-primary)]">
          <Lock size={14} weight="bold"/> lock it in
        </button>
        <button onClick={cancel} className="btn-hard bg-white rounded-md px-4 py-2 text-sm">cancel</button>
      </div>

      {rewriteOpen && (
        <div className="mt-5 border-[1.5px] border-[var(--brand-ink)] rounded-md p-4 bg-white" data-testid="ai-rewrite-panel">
          <p className="label-tiny mb-2">/ai rewriter</p>
          <textarea value={rewriteText} onChange={(e) => setRewriteText(e.target.value)}
            placeholder="paste your messy bullets here…"
            data-testid="ai-rewrite-input"
            className="w-full card-hard rounded-md px-3 py-2 min-h-[80px] bg-white"/>
          <div className="mt-3 flex gap-2">
            <button onClick={doRewrite} disabled={rewriting || !rewriteText} data-testid="ai-rewrite-run"
              className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-1.5 text-xs disabled:opacity-60 hover:bg-[var(--brand-ink)]">
              {rewriting ? "thinking…" : "rewrite"}
            </button>
            <button onClick={() => setRewriteOpen(false)} className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs">close</button>
          </div>
          {rewriteResult && (
            <div className="mt-3 border-[1.5px] border-[var(--brand-ink)] rounded-md p-3 bg-[var(--pastel-mint)] text-sm whitespace-pre-line" data-testid="ai-rewrite-result">
              {rewriteResult}
              <div className="mt-3 flex gap-2">
                <button onClick={applyRewrite} data-testid="ai-rewrite-apply"
                  className="btn-hard bg-white rounded-md px-3 py-1 text-xs">use this</button>
                <button onClick={() => copyToClipboard(rewriteResult, "Result copied")}
                  className="btn-hard bg-white rounded-md px-3 py-1 text-xs flex items-center gap-1">
                  <Copy size={12}/> copy
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <section className="card-hard rounded-md bg-white" data-testid={`section-${resource}`}>
      <div className={`flex items-center justify-between p-5 border-b-[1.5px] border-[var(--brand-ink)] ${accent}`}>
        <div>
          <p className="label-tiny">/{resource}</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight">{schema.title}</h2>
          {schema.helper && <p className="text-xs text-[var(--brand-ink)]/60 mt-1">{schema.helper}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {items.length > 1 && (
            <button
              onClick={allExpanded ? collapseAll : expandAll}
              data-testid={`toggle-all-${resource}`}
              title={allExpanded ? "collapse all" : "expand all"}
              className="btn-hard bg-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5"
            >
              {allExpanded ? <CaretDoubleUp size={13} weight="bold"/> : <CaretDoubleDown size={13} weight="bold"/>}
              <span className="hidden sm:inline">{allExpanded ? "collapse all" : "expand all"}</span>
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={clearAll}
              data-testid={`clear-all-${resource}`}
              title={`wipe all ${items.length} ${resource} entries`}
              className="btn-hard bg-[#B91C1C] text-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5"
            >
              <TrashSimple size={13} weight="bold"/>
              <span className="hidden sm:inline">delete all</span>
            </button>
          )}
          <button onClick={openNew} data-testid={`add-${resource}`}
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm flex items-center gap-1.5 hover:bg-[var(--brand-ink)]">
            <Plus weight="bold" size={14}/> add
          </button>
        </div>
      </div>

      <div className="p-5">
        {items.length === 0 && editing !== "new" && (
          <p className="text-sm text-[var(--brand-muted)]">nothing here yet. hit + add to put something in the vault.</p>
        )}

        {/* New entry: render the editor at the top of the list */}
        {editing === "new" && <div className="mb-3">{renderEditor()}</div>}

        <ul className="space-y-3">
          {items.map((it) => (
            editing === it.id ? (
              <li key={it.id} data-testid={`item-${resource}-${it.id}`}>
                {renderEditor()}
              </li>
            ) : (
              <li key={it.id} data-testid={`item-${resource}-${it.id}`}
                draggable
                onDragStart={(e) => onDragStart(e, it.id)}
                onDragOver={(e) => onDragOver(e, it.id)}
                onDrop={(e) => onDrop(e, it.id)}
                onDragEnd={onDragEnd}
                className={`border-[1.5px] border-[var(--brand-ink)] rounded-md p-4 bg-white transition ${
                  dragOver === it.id && dragId !== it.id ? "ring-4 ring-[var(--brand-primary)]" : ""
                } ${dragId === it.id ? "opacity-40" : ""}`}>
                <div className="flex justify-between items-start">
                  <div className="flex gap-3 min-w-0 flex-1 cursor-pointer"
                    onClick={() => toggleExpand(it.id)}
                    data-testid={`row-toggle-${resource}-${it.id}`}
                    title={expanded.has(it.id) ? "click to collapse" : "click to expand"}>
                    <span className="cursor-grab active:cursor-grabbing text-[var(--brand-muted)] mt-0.5"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`drag-handle-${resource}-${it.id}`}>
                      <DotsSixVertical size={18}/>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium break-words">{schema.primary(it)}</p>
                      <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-1">{schema.secondary(it)}</p>
                      {it.skills && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`skills-${resource}-${it.id}`}>
                          {splitSkills(it.skills).map((s, i) => (
                            <span key={`${s}-${i}`}
                              className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-mint)] font-medium">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-3 shrink-0">
                    <button onClick={() => toggleExpand(it.id)}
                      data-testid={`expand-${resource}-${it.id}`}
                      title={expanded.has(it.id) ? "hide details" : "show details"}
                      className="p-1.5 hover:bg-[var(--brand-bg)] rounded">
                      {expanded.has(it.id) ? <CaretUp size={16}/> : <CaretDown size={16}/>}
                    </button>
                    <button onClick={() => copyToClipboard(schema.copyText(it), "Entry copied")}
                      data-testid={`copy-${resource}-${it.id}`}
                      title="copy entry as text"
                      className="p-1.5 hover:bg-[var(--brand-bg)] rounded"><Copy size={16}/></button>
                    <button onClick={() => copyAsMarkdown(asMarkdown[resource], it, "Copied as Markdown")}
                      data-testid={`copy-md-${resource}-${it.id}`}
                      title="copy as Markdown"
                      className="p-1.5 hover:bg-[var(--brand-bg)] rounded"><MarkdownLogo size={16}/></button>
                    <button onClick={() => openEdit(it)} data-testid={`edit-${resource}-${it.id}`}
                      title="edit this entry"
                      className="p-1.5 hover:bg-[var(--brand-bg)] rounded"><PencilSimple size={16}/></button>
                    <button onClick={() => remove(it.id)} data-testid={`delete-${resource}-${it.id}`}
                      className="p-1.5 hover:bg-[var(--brand-bg)] rounded"><Trash size={16}/></button>
                  </div>
                </div>
                {expanded.has(it.id) && <FieldsList item={it} schema={schema} resource={resource}/>}
              </li>
            )
          ))}
        </ul>
      </div>
    </section>
  );
}

export default ResourceSection;
